import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareButton from "@/components/ShareButton";
import StoryBoard from "@/components/StoryBoard";
import SavedInstitutions from "@/components/SavedInstitutions";
import AlumniImpact from "@/components/AlumniImpact";
import SocialPulseBadge from "@/components/SocialPulseBadge";
import {
  MapPin, PencilSimple, GraduationCap, EnvelopeSimple, Phone,
  Sparkle, Trophy, Circle, ChatCircleText,
  LinkedinLogo, XLogo, FacebookLogo, InstagramLogo, YoutubeLogo, GithubLogo, Globe,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const SOCIAL_LINKS = [
  { key: "linkedin", label: "LinkedIn", Icon: LinkedinLogo },
  { key: "twitter", label: "X", Icon: XLogo },
  { key: "facebook", label: "Facebook", Icon: FacebookLogo },
  { key: "instagram", label: "Instagram", Icon: InstagramLogo },
  { key: "youtube", label: "YouTube", Icon: YoutubeLogo },
  { key: "github", label: "GitHub", Icon: GithubLogo },
  { key: "website", label: "Website", Icon: Globe },
];

export default function ProfileView() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const isMe = !slug;                       // /profile (no slug) -> fetch /profiles/me
  const viewingSelf = isMe || (!!user && !!p && p.user_id === user.id); // own profile via /u/<own-slug>

  const load = () => {
    const url = isMe ? "/profiles/me" : `/profiles/${slug}`;
    api.get(url).then((r) => setP(r.data)).catch(() => setP(false));
  };
  useEffect(() => { load(); }, [slug, isMe]);

  if (p === null) return <div className="p-12 text-center text-muted-foreground">Loading profile…</div>;
  if (p === false) return <div className="p-12 text-center text-muted-foreground">Profile not found.</div>;

  const initials = (p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const shareTarget = `/u/${p.public_slug || p.user_id}`;

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <Card className="overflow-hidden border-border/60 shadow-sm">
        <div className="relative h-36 bg-primary grain sm:h-44" />
        <div className="px-6 pb-6 sm:px-8">
          <div className="-mt-14 flex flex-wrap items-end justify-between gap-4">
            <Avatar className="h-28 w-28 border-4 border-card shadow-md">
              <AvatarImage src={fileUrl(p.avatar_file_id)} />
              <AvatarFallback className="bg-secondary text-secondary-foreground text-2xl font-heading">{initials}</AvatarFallback>
            </Avatar>
            <div className="mb-1 flex gap-2">
              <ShareButton targetPath={shareTarget} entityType="profile" entityId={p.user_id} title={p.name} />
              {viewingSelf && <Button className="gap-2 rounded-full" onClick={() => nav("/profile/edit")} data-testid="edit-profile-btn"><PencilSimple size={16} /> Edit</Button>}
              {!viewingSelf && user && (
                <Button className="gap-2 rounded-full" data-testid="message-member-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/dm/start", { user_id: p.user_id });
                      nav(`/messages?c=${data.id}`);
                    } catch (e) { toast.error("Could not open chat. Please sign in."); }
                  }}>
                  <ChatCircleText size={16} /> Message
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-3xl font-medium tracking-tight" data-testid="profile-name">{p.name}</h1>
              <SocialPulseBadge verified={p.socialpulse_verified} />
            </div>
            {p.headline && <p className="mt-1 text-lg text-muted-foreground">{p.headline}</p>}
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {p.location && <span className="flex items-center gap-1.5"><MapPin size={15} /> {p.location}</span>}
              {p.email && <span className="flex items-center gap-1.5"><EnvelopeSimple size={15} /> {p.email}</span>}
              {p.phone && <span className="flex items-center gap-1.5"><Phone size={15} /> {p.phone}</span>}
            </div>
            {p.social && Object.values(p.social).some(Boolean) && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="profile-socials">
                {SOCIAL_LINKS.filter((s) => p.social[s.key]).map((s) => (
                  <a key={s.key} href={p.social[s.key]} target="_blank" rel="noreferrer" title={s.label} data-testid={`profile-social-${s.key}`}
                    className="grid h-9 w-9 place-items-center rounded-full border border-border/60 text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
                    <s.Icon size={17} weight="fill" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {viewingSelf && (
            <div className="mt-5 rounded-xl bg-muted/50 p-4">
              <div className="mb-2 flex items-center justify-between text-sm"><span className="font-medium">Profile completion</span><span className="font-semibold text-secondary">{p.completion}%</span></div>
              <Progress value={p.completion} className="h-1.5" />
            </div>
          )}

          {p.bio && <p className="mt-6 max-w-2xl leading-relaxed text-foreground/90">{p.bio}</p>}
        </div>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Education timeline */}
        <Card className="border-border/60 p-6 shadow-sm lg:col-span-2">
          <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-medium"><GraduationCap className="text-secondary" size={22} weight="duotone" /> Educational journey</h2>
          {(!p.education || p.education.length === 0) && <p className="text-sm text-muted-foreground">No education added yet.</p>}
          <ol className="relative space-y-8 border-l-2 border-border pl-6">
            {(p.education || []).map((e, i) => (
              <li key={e.id || i} className="relative animate-fade-up" data-testid="education-item" style={{ animationDelay: `${i * 0.05}s` }}>
                <span className="absolute -left-[31px] grid h-5 w-5 place-items-center rounded-full bg-secondary text-secondary-foreground"><Circle size={8} weight="fill" /></span>
                <div className="font-heading text-lg font-medium">{e.institution}</div>
                <div className="text-sm text-secondary">{[e.degree, e.course].filter(Boolean).join(" · ")}</div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  {e.department && <span>{e.department}</span>}
                  {e.class_name && <span>Class {e.class_name}</span>}
                  {(e.batch || e.start_year) && <span>{e.batch || `${e.start_year} – ${e.end_year}`}</span>}
                </div>
              </li>
            ))}
          </ol>
        </Card>

        <div className="space-y-6">
          {p.skills?.length > 0 && (
            <Card className="border-border/60 p-6 shadow-sm">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-medium"><Sparkle className="text-secondary" size={18} weight="duotone" /> Skills</h2>
              <div className="flex flex-wrap gap-2">{p.skills.map((s) => <Badge key={s} variant="secondary" className="rounded-full font-normal">{s}</Badge>)}</div>
            </Card>
          )}
          {p.achievements?.length > 0 && (
            <Card className="border-border/60 p-6 shadow-sm">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-medium"><Trophy className="text-secondary" size={18} weight="duotone" /> Achievements</h2>
              <ul className="space-y-2 text-sm text-foreground/90">{p.achievements.map((a, i) => <li key={i} className="flex gap-2"><span className="text-secondary">•</span> {a}</li>)}</ul>
            </Card>
          )}
          {viewingSelf && <Link to="/resumes"><Button variant="outline" className="w-full rounded-full">Build a resume from this profile</Button></Link>}
        </div>
      </div>

      <div className="mt-6">
        <SavedInstitutions profile={p} isMe={viewingSelf} onChange={load} />
      </div>

      <div className="mt-6">
        <AlumniImpact userId={p.user_id} self={viewingSelf} />
      </div>

      <Card className="mt-6 border-border/60 p-6 shadow-sm">
        <StoryBoard boardType="profile" boardId={p.user_id} canPost={!!user} title={viewingSelf ? "Your storyboard" : `Leave a note for ${p.name?.split(" ")[0]}`} />
      </Card>
    </main>
  );
}
